import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { ArrowUpDown } from 'lucide-react'
import { memo, useState } from 'react'
import { ErrorBoundary } from 'v2/shared/components/error-boundary'
import { usePhoneStore } from 'v2/state/phone'
import { FlowTypes } from 'v2/types'
import { useFlowStore } from '../store'
import { Analysis } from './analysis'
import { PanelWrapper } from './components/panel-wrapper'
import { ResponseCounter } from './components/response-counter'
import { EventInterface } from './event-interface'
import { CallControls } from './event-interface/call-controls'
import { ChatInput } from './event-interface/chat-input'
import { usePathHighlighter } from './hooks/use-node-highlight'
import { useRunEvents } from './hooks/use-run-events'

/**
 * Chat transcripts carry product carousels, whose cards are 242px — wider than what the
 * default panel leaves once the timestamp column is taken out, so a single card had to be
 * scrolled to be read. Returns undefined for every other channel, which keeps the panel's
 * own default.
 */
export const runPanelWidth = (isChat: boolean) =>
  isChat ? { minimized: 280, normal: 520 } : undefined

export const RunInfo = memo(() => {
  useRunEvents()
  const [showAnalysis, setShowAnalysis] = useState(true)
  const activeCall = usePhoneStore((state) => state.activeCall)
  const { executionHistory } = usePathHighlighter('current-only')
  // The wide panel exists for chat's product carousels; SMS keeps the default.
  const isChatFlow = useFlowStore((state) => state.currentFlowType) === FlowTypes.incoming_chat

  if (activeCall?.contactName === 'Playground' || executionHistory.length !== 0) {
    const toggleMode = () => setShowAnalysis(!showAnalysis)

    const toggleButton = (
      <Tooltip content={showAnalysis ? 'Switch to Transcript mode' : 'Switch to Analysis mode'}>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-secondary hover:bg-muted"
          onClick={toggleMode}
          aria-label={showAnalysis ? 'Switch to Transcript mode' : 'Switch to Analysis mode'}
          data-testid="button-toggle-mode"
        >
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      </Tooltip>
    )

    const footer = (
      <div className="bg-card">
        {activeCall ? <CallControls /> : <ChatInput placeholder="Your message" />}
      </div>
    )

    return (
      <ErrorBoundary>
        <div data-testid="run-info" className="flex justify-between bottom-3 h-0 relative">
          <PanelWrapper
            title={showAnalysis ? 'Analysis' : 'Transcript'}
            subtitle={showAnalysis ? <ResponseCounter /> : undefined}
            headerBgColor="bg-background"
            headerTextColor="text-primary"
            toggleButton={toggleButton}
            className="ml-4 absolute bottom-0 left-0"
            width={runPanelWidth(isChatFlow)}
            footer={footer}
          >
            {showAnalysis ? <Analysis /> : <EventInterface />}
          </PanelWrapper>
        </div>
      </ErrorBoundary>
    )
  }
  return null
})
